import { useState, useEffect } from "react";
import { db } from "../firebase/config";
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  onSnapshot,
} from "firebase/firestore";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import "../styles/kpi-list.css";

const DRIVE_FOLDER_ID = "1MPCYteG3VKHLkMlVgTpibzqMBquGRiHb";

export default function KpiList({ perusahaan, tahun, role }) {
  const [data, setData] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [googleToken, setGoogleToken] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingId, setUploadingId] = useState(null);
  const [newKpi, setNewKpi] = useState({
    indikator: "",
    keterangan: "",
    satuan: "",
    bobot: "",
    target: "",
  });

  const login = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      setGoogleToken(tokenResponse.access_token);
    },
    onError: (error) => {
      console.error("Login Failed:", error);
      alert("Login gagal");
    },
    scope: "https://www.googleapis.com/auth/drive.file",
  });

  useEffect(() => {
    if (!perusahaan || !tahun) return;

    const unsub = onSnapshot(
      collection(db, "kpi_collections", perusahaan, tahun),
      (snapshot) => {
        setData(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      }
    );

    return () => unsub();
  }, [perusahaan, tahun]);

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditData({
      target_sd_sekarang: item.target_sd_sekarang || "",
      realisasi: item.realisasi || "",
      pencapaian: item.pencapaian || "",
      nilai: item.nilai || "",
      evidence: item.evidence || "",
      bobot: item.bobot || "",
    });
  };

  const handleSave = async (id) => {
    try {
      await updateDoc(
        doc(db, "kpi_collections", perusahaan, tahun, id),
        editData
      );
      setEditingId(null);
    } catch (error) {
      console.error("Update Error:", error);
    }
  };

  const handleFileUpload = async (e, kpiId) => {
    if (!googleToken) return alert("Login Google dulu");
    const file = e.target.files[0];
    if (!file) return;

    setUploadingId(kpiId);
    setUploadProgress(0);

    try {
      const fileId = await uploadFile(file, kpiId);
      await setFilePublic(fileId);
      const fileUrl = `https://drive.google.com/file/d/${fileId}/view`;

      await updateDoc(doc(db, "kpi_collections", perusahaan, tahun, kpiId), {
        evidence: fileUrl,
      });

      setEditData((prev) => ({ ...prev, evidence: fileUrl }));
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setUploadingId(null);
      setUploadProgress(0);
    }
  };

  const uploadFile = async (file, kpiId) => {
    const currentKpi = data.find((item) => item.id === kpiId);
    const perusahaanName = perusahaan.replace(/\s+/g, "_");
    const tahunName = tahun.replace(/\s+/g, "_");
    const indikator =
      currentKpi.indikator?.replace(/[^\w\s]/gi, "").replace(/\s+/g, "_") ||
      "indikator";

    const perusahaanFolderId = await getOrCreateFolder(perusahaanName);
    const tahunFolderId = await getOrCreateFolder(
      tahunName,
      perusahaanFolderId
    );

    const fileName = `kpi_${perusahaanName}_${tahunName}_${indikator}${file.name.substring(
      file.name.lastIndexOf(".")
    )}`;

    const metadata = {
      name: fileName,
      parents: [tahunFolderId],
      mimeType: file.type,
    };

    const formData = new FormData();
    formData.append(
      "metadata",
      new Blob([JSON.stringify(metadata)], { type: "application/json" })
    );
    formData.append("file", file);

    const { data: uploaded } = await axios.post(
      "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart",
      formData,
      {
        headers: {
          Authorization: `Bearer ${googleToken}`,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (e) => {
          const progress = Math.round((e.loaded * 100) / e.total);
          setUploadProgress(progress);
        },
      }
    );

    return uploaded.id;
  };

  const setFilePublic = async (fileId) => {
    await axios.post(
      `https://www.googleapis.com/drive/v3/files/${fileId}/permissions`,
      {
        role: "reader",
        type: "anyone",
      },
      {
        headers: {
          Authorization: `Bearer ${googleToken}`,
          "Content-Type": "application/json",
        },
      }
    );
  };

  // ➕ Tambah KPI
  const handleAddKpi = async () => {
    try {
      await addDoc(collection(db, "kpi_collections", perusahaan, tahun), {
        ...newKpi,
        target_sd_sekarang: "",
        realisasi: "",
        pencapaian: "",
        nilai: "",
        evidence: "",
      });
      setNewKpi({
        indikator: "",
        keterangan: "",
        satuan: "",
        bobot: "",
        target: "",
      });
    } catch (err) {
      console.error("Gagal tambah KPI", err);
    }
  };

  // Cek apakah folder sudah ada, jika belum maka buat
  const getOrCreateFolder = async (folderName, parentId = null) => {
    const query = `'${
      parentId || DRIVE_FOLDER_ID
    }' in parents and name='${folderName}' and mimeType='application/vnd.google-apps.folder' and trashed=false`;

    const res = await axios.get(
      `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(
        query
      )}`,
      {
        headers: {
          Authorization: `Bearer ${googleToken}`,
        },
      }
    );

    if (res.data.files.length > 0) {
      return res.data.files[0].id;
    }

    // Buat folder baru
    const folderMeta = {
      name: folderName,
      mimeType: "application/vnd.google-apps.folder",
      parents: [parentId || DRIVE_FOLDER_ID],
    };

    const { data } = await axios.post(
      "https://www.googleapis.com/drive/v3/files",
      folderMeta,
      {
        headers: {
          Authorization: `Bearer ${googleToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    return data.id;
  };

  const handleDeleteEvidence = async (kpiId, evidenceUrl) => {
    if (!googleToken || !evidenceUrl) return;

    const fileId = extractFileId(evidenceUrl);

    try {
      await axios.delete(
        `https://www.googleapis.com/drive/v3/files/${fileId}`,
        {
          headers: {
            Authorization: `Bearer ${googleToken}`,
          },
        }
      );

      await updateDoc(doc(db, "kpi_collections", perusahaan, tahun, kpiId), {
        evidence: "",
      });

      setData(
        data.map((item) =>
          item.id === kpiId ? { ...item, evidence: "" } : item
        )
      );

      if (editingId === kpiId) {
        setEditData((prev) => ({ ...prev, evidence: "" }));
      }

      alert("File berhasil dihapus dari Google Drive");
    } catch (error) {
      console.error("Gagal hapus file:", error);
      alert("Gagal menghapus file");
    }
  };

  const extractFileId = (url) => {
    const match = url.match(/\/d\/(.+?)\//);
    return match ? match[1] : null;
  };

  const formatValue = (value, satuan) => {
    if (!value) return value;

    const satuanLower = satuan?.toLowerCase() || "";

    // Handle persen
    if (satuanLower.includes("persen")) {
      return `${value}%`;
    }

    // Handle rupiah
    if (satuanLower.includes("rupiah")) {
      return `Rp ${parseFloat(value).toLocaleString("id-ID")}`;
    }

    // Default return value as is
    return value;
  };

  // 🔁 Hitung otomatis pencapaian & nilai
  useEffect(() => {
    if (
      editingId &&
      editData.realisasi &&
      editData.target_sd_sekarang &&
      editData.bobot
    ) {
      const realisasi = parseFloat(editData.realisasi) || 0;
      const targetNow = parseFloat(editData.target_sd_sekarang) || 0;
      const bobot = parseFloat(editData.bobot) || 0;

      const pencapaian = targetNow > 0 ? (realisasi / targetNow) * 100 : 0;
      const nilai = pencapaian * (bobot / 100);

      setEditData((prev) => ({
        ...prev,
        pencapaian: pencapaian.toFixed(2),
        nilai: nilai.toFixed(2),
      }));
    }
  }, [
    editData.realisasi,
    editData.target_sd_sekarang,
    editData.bobot,
    editingId,
  ]);

  return (
    <div className="kpi-list-wrapper">
      {role === "pimpinan" && (
        <>
          {/*<div className="kpi-form-add">
            <h3>Tambah KPI Baru</h3>
            <div className="form-row">
              {["indikator", "keterangan", "satuan", "bobot", "target"].map(
                (key) => (
                  <div className="form-group" key={key}>
                    <label>{key.toUpperCase()}</label>
                    <input
                      value={newKpi[key]}
                      onChange={(e) =>
                        setNewKpi({ ...newKpi, [key]: e.target.value })
                      }
                    />
                  </div>
                )
              )}
            </div>
            <button className="submit-btn" onClick={handleAddKpi}>
              Tambah
            </button>
          </div>*/}

          <div className="google-auth-section">
            {!googleToken ? (
              <button onClick={login} className="google-login-btn">
                <img
                  src="https://developers.google.com/identity/images/g-logo.png"
                  alt="Google"
                  className="google-logo"
                />
                Login dengan Google untuk Upload File
              </button>
            ) : (
              <div className="upload-status">
                <span>Sudah terhubung dengan Google Drive</span>
                <button
                  onClick={() => setGoogleToken(null)}
                  className="logout-btn"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {data.length === 0 ? (
        <p className="no-data">Belum ada data KPI</p>
      ) : (
        <div className="table-responsive">
          <table className="kpi-table">
            <thead>
              <tr>
                <th>No</th>
                <th>Indikator</th>
                <th>Keterangan</th>
                <th>Satuan</th>
                <th>Bobot</th>
                <th>Target</th>
                <th>Target s.d Sekarang</th>
                <th>Realisasi</th>
                <th>Pencapaian</th>
                <th>Nilai</th>
                <th>Evidence</th>
                {role === "pimpinan" && <th>Aksi</th>}
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr key={row.id}>
                  <td>{idx + 1}</td>
                  <td>{row.indikator}</td>
                  <td>{row.keterangan}</td>
                  <td>{row.satuan}</td>
                  <td>{row.bobot}</td>
                  <td>{formatValue(row.target, row.satuan)}</td>

                  <td>
                    {editingId === row.id ? (
                      <input
                        type="text"
                        value={editData.target_sd_sekarang}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            target_sd_sekarang: e.target.value,
                          })
                        }
                        className="editable-input"
                      />
                    ) : (
                      formatValue(row.target_sd_sekarang, row.satuan)
                    )}
                  </td>

                  <td>
                    {editingId === row.id ? (
                      <input
                        type="text"
                        value={editData.realisasi}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            realisasi: e.target.value,
                          })
                        }
                        className="editable-input"
                      />
                    ) : (
                      formatValue(row.realisasi, row.satuan)
                    )}
                  </td>

                  <td>
                    {editingId === row.id ? (
                      <input
                        type="text"
                        value={editData.pencapaian}
                        readOnly
                        className="editable-input"
                      />
                    ) : (
                      row.pencapaian
                    )}
                  </td>

                  <td>
                    {editingId === row.id ? (
                      <input
                        type="text"
                        value={editData.nilai}
                        readOnly
                        className="editable-input"
                      />
                    ) : (
                      row.nilai
                    )}
                  </td>

                  <td>
                    {row.evidence ? (
                      <div className="evidence-actions">
                        <a
                          href={row.evidence}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="evidence-link"
                        >
                          📄 Lihat
                        </a>
                        {editingId === row.id && (
                          <button
                            className="delete-file-btn"
                            onClick={() =>
                              handleDeleteEvidence(row.id, row.evidence)
                            }
                            title="Hapus Evidence"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    ) : editingId === row.id ? (
                      "-"
                    ) : (
                      <span className="no-evidence">Belum ada</span>
                    )}
                  </td>

                  {role === "pimpinan" && (
                    <td className="action-cell">
                      {editingId === row.id ? (
                        <div className="edit-actions">
                          <button
                            onClick={() => handleSave(row.id)}
                            className="save-btn"
                          >
                            Simpan
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="cancel-btn"
                          >
                            Batal
                          </button>
                          <label
                            className={`file-upload-label ${
                              uploadingId === row.id ? "uploading" : ""
                            }`}
                          >
                            {uploadingId === row.id
                              ? `Uploading... ${uploadProgress}%`
                              : "Upload Evidence"}
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.jpg,.png"
                              disabled={uploadingId === row.id}
                              onChange={(e) => handleFileUpload(e, row.id)}
                            />
                          </label>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEdit(row)}
                          className="edit-btn"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {role === "pimpinan" && (
        <div className="kpi-score-total">
          Total Skor:{" "}
          {data
            .reduce((acc, item) => acc + (parseFloat(item.nilai) || 0), 0)
            .toFixed(2)}
        </div>
      )}
    </div>
  );
}
